import {useMemo, useState} from "react";
import {App as AntdApp, Alert, Empty, Modal, Select, Table, Tag, Tooltip} from "antd";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useAddCompetitorMutation,
	useAnalyzeCompetitorLabsMutation,
	useCompetitorLabsGapMutation,
	useCompetitorLabsSuggestMutation,
	useGetCompetitorLabsKeywordsQuery,
	useGetCompetitorLabsQuery,
} from "~/reduxs/api/competitorApiSlice";

const nf = (v) => Number(v || 0).toLocaleString('vi-VN');
const dash = <span style={{color: '#9ca3af'}}>—</span>;

/** Volume gọn (1.2k / 35k / 1,2tr). */
const volText = (v) => {
	if (v === null || v === undefined) return '—';
	if (v >= 1000000) return `${(v / 1000000).toLocaleString('vi-VN', {maximumFractionDigits: 1})}tr`;
	if (v >= 1000) return `${(v / 1000).toLocaleString('vi-VN', {maximumFractionDigits: 1})}k`;
	return nf(v);
};

/**
 * Tab "Organic (DFS)" của tool Phân tích đối thủ (§8 bước 4 — DataForSEO Labs):
 *  - Bảng own + đối thủ: số từ khoá organic TUYỆT ĐỐI, traffic ước tính (ETV), top 3/10 —
 *    nút "Phân tích" per domain (cache chung 30 ngày; miss trừ 1 lượt của gói).
 *  - Modal top từ khoá organic của 1 domain (từ cache, 0đ).
 *  - Keyword gap THẬT: từ khoá đối thủ có hạng mà mình không (domain_intersection).
 *  - Gợi ý đối thủ (competitors_domain) + nút thêm thẳng vào danh sách.
 */
function CompetitorLabs({projectId}) {

	const {notification} = AntdApp.useApp();

	const {data, isLoading, isFetching} = useGetCompetitorLabsQuery(projectId);
	const [analyze] = useAnalyzeCompetitorLabsMutation();
	const [runGap, {isLoading: gapLoading}] = useCompetitorLabsGapMutation();
	const [runSuggest, {isLoading: suggestLoading}] = useCompetitorLabsSuggestMutation();
	const [addCompetitor] = useAddCompetitorMutation();

	const [analyzingId, setAnalyzingId] = useState(null);
	const [keywordsOf, setKeywordsOf] = useState(null); // {competitor_id, domain, name}
	const [gapCompetitorId, setGapCompetitorId] = useState(null);
	const [gapResult, setGapResult] = useState(null);
	const [suggestions, setSuggestions] = useState(null);
	const [addingDomain, setAddingDomain] = useState(null);

	const domains = useMemo(() => data?.domains || [], [data]);
	const quota = data?.quota || {};
	const rivals = domains.filter((d) => !d.is_own);

	if (isLoading) return <Loading />;
	if (!data) return null;

	if (quota.can === false) {
		return (
			<Alert type="warning" showIcon style={{marginTop: 8}}
				message="Gói của bạn chưa hỗ trợ số liệu Organic tuyệt đối"
				description={<>
					Số từ khoá organic, traffic ước tính, keyword gap thật và gợi ý đối thủ có ở gói Pro trở lên.
					<div style={{marginTop: 10}}>
						<Button primary small onClick={() => (window.location.href = '/subscription')}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-dots" />}>Xem các gói</Button>
					</div>
				</>} />
		);
	}

	const doAnalyze = async (row) => {
		setAnalyzingId(row.competitor_id);
		try {
			const res = await analyze({projectId, competitorId: row.competitor_id}).unwrap();
			notification.success({
				message: `Đã phân tích ${res?.domain || row.domain}`,
				description: res?.cached ? 'Dùng dữ liệu cache chung (không tốn lượt).' : 'Đã lấy dữ liệu mới (1 lượt của gói).',
			});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không phân tích được domain này')});
		} finally {
			setAnalyzingId(null);
		}
	};

	const doGap = async () => {
		if (!gapCompetitorId) return;
		try {
			const res = await runGap({projectId, competitorId: gapCompetitorId}).unwrap();
			setGapResult(res);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lấy được keyword gap')});
		}
	};

	const doSuggest = async () => {
		try {
			const res = await runSuggest(projectId).unwrap();
			setSuggestions(res);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lấy được gợi ý đối thủ')});
		}
	};

	const doAddSuggestion = async (row) => {
		setAddingDomain(row.domain);
		try {
			await addCompetitor({projectId, url: `https://${row.domain}`}).unwrap();
			notification.success({message: 'Đã thêm đối thủ', description: row.domain});
			setSuggestions((prev) => prev ? {
				...prev,
				suggestions: prev.suggestions.map((s) => (s.domain === row.domain ? {...s, added: true} : s)),
			} : prev);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thêm được đối thủ')});
		} finally {
			setAddingDomain(null);
		}
	};

	const overviewCols = [
		{title: 'Website', key: 'name', ellipsis: true,
			render: (_, r) => (
				<span>
					{r.is_own ? <strong style={{color: '#ca8a04'}}>{r.domain} <FontAwesomeIcon icon="fa-solid fa-star" /></strong> : r.domain}
					{r.name && r.name !== r.domain && <div style={{fontSize: 12, color: '#9ca3af'}}>{r.name}</div>}
				</span>
			)},
		{title: <Tooltip title="Tổng số từ khoá organic domain đang có hạng">Từ khoá organic</Tooltip>,
			key: 'kw', width: 140, align: 'center',
			sorter: (a, b) => (a.overview?.keywords_count ?? -1) - (b.overview?.keywords_count ?? -1),
			render: (_, r) => (r.overview ? <strong>{nf(r.overview.keywords_count)}</strong> : dash)},
		{title: <Tooltip title="Traffic organic ước tính / tháng (ETV)">Traffic ước tính</Tooltip>,
			key: 'etv', width: 140, align: 'center', responsive: ['md'],
			sorter: (a, b) => (a.overview?.etv ?? -1) - (b.overview?.etv ?? -1),
			render: (_, r) => (r.overview ? volText(Math.round(r.overview.etv)) : dash)},
		{title: 'Top 3', key: 'top3', width: 90, align: 'center', responsive: ['md'],
			render: (_, r) => (r.overview ? nf(r.overview.top3) : dash)},
		{title: 'Top 10', key: 'top10', width: 90, align: 'center', responsive: ['lg'],
			render: (_, r) => (r.overview ? nf(r.overview.top10) : dash)},
		{title: '', key: 'action', align: 'right', width: 190,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 6}}>
					<Button small white loading={analyzingId === r.competitor_id} onClick={() => doAnalyze(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-column" />}>
						{r.overview ? (r.fresh ? 'Làm mới' : 'Phân tích lại') : 'Phân tích'}
					</Button>
					{r.has_keywords && (
						<Tooltip title="Top từ khoá organic (từ cache)">
							<Button small white onClick={() => setKeywordsOf(r)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-list" />} />
						</Tooltip>
					)}
				</span>
			)},
	];

	const gapCols = [
		{title: 'Từ khoá đối thủ có hạng (mình không)', dataIndex: 'keyword', key: 'keyword', ellipsis: true},
		{title: 'Volume', dataIndex: 'volume', key: 'volume', width: 100, align: 'center',
			sorter: (a, b) => (a.volume ?? -1) - (b.volume ?? -1), defaultSortOrder: 'descend',
			render: (v) => (v === null ? dash : volText(v))},
		{title: 'CPC ($)', dataIndex: 'cpc', key: 'cpc', width: 90, align: 'center', responsive: ['md'],
			render: (v) => (v === null ? dash : `$${v}`)},
		{title: 'Vị trí đối thủ', dataIndex: 'rival_position', key: 'pos', width: 120, align: 'center',
			render: (v) => (v === null ? dash : <Tag color={v <= 3 ? 'green' : v <= 10 ? 'gold' : 'default'}>#{v}</Tag>)},
		{title: 'Trang đối thủ', dataIndex: 'rival_url', key: 'url', ellipsis: true, responsive: ['lg'],
			render: (v) => (v ? <a href={v} target="_blank" rel="noreferrer" style={{fontSize: 12}}>{v}</a> : dash)},
	];

	const suggestCols = [
		{title: 'Domain', dataIndex: 'domain', key: 'domain', ellipsis: true},
		{title: <Tooltip title="Số từ khoá cùng có hạng với website của bạn — càng nhiều càng là đối thủ trực tiếp">Từ khoá giao nhau</Tooltip>,
			dataIndex: 'intersections', key: 'intersections', width: 150, align: 'center',
			sorter: (a, b) => a.intersections - b.intersections, defaultSortOrder: 'descend', render: nf},
		{title: 'Từ khoá organic', dataIndex: 'keywords_count', key: 'kw', width: 140, align: 'center', responsive: ['md'],
			render: (v) => (v ? nf(v) : dash)},
		{title: 'Traffic ước tính', dataIndex: 'etv', key: 'etv', width: 130, align: 'center', responsive: ['lg'],
			render: (v) => (v ? volText(Math.round(v)) : dash)},
		{title: '', key: 'action', align: 'right', width: 130,
			render: (_, r) => r.added
				? <Tag color="green">Đã có</Tag>
				: <Button small white loading={addingDomain === r.domain} onClick={() => doAddSuggestion(r)}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>Thêm đối thủ</Button>},
	];

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="Số liệu organic TUYỆT ĐỐI (khác các tab nội bộ dùng dữ liệu GSC/crawl). Dữ liệu cache dùng chung 30 ngày — domain đã được phân tích gần đây sẽ không tốn lượt." />

			{quota.sandbox && (
				<Alert type="warning" showIcon style={{marginBottom: 14}}
					message="Đang dùng dữ liệu mẫu (chế độ thử nghiệm) — số liệu là demo, không phải của các domain này." />
			)}

			{typeof quota.remaining === 'number' && (
				<div style={{fontSize: 12, color: '#6b7280', marginBottom: 12}}>
					Lượt phân tích sâu: {quota.used}/{quota.limit} mỗi chu kỳ ({quota.remaining} còn lại)
					{quota.reset_at ? <> · làm mới {quota.reset_at}</> : <> · làm mới khi gia hạn gói</>}
				</div>
			)}

			{/* 1. Bảng tổng quan organic own + đối thủ */}
			<h3 style={{fontSize: 15, fontWeight: 600, margin: '4px 0 10px'}}>Tổng quan organic</h3>
			<Table
				rowKey="competitor_id"
				size="small"
				loading={isFetching}
				columns={overviewCols}
				dataSource={domains}
				scroll={{x: 'max-content'}}
				pagination={false}
			/>

			{/* 2. Keyword gap thật */}
			<h3 style={{fontSize: 15, fontWeight: 600, margin: '24px 0 10px'}}>
				Keyword gap thật{' '}
				<Tooltip title="Từ khoá đối thủ ĐANG CÓ HẠNG trên Google mà website bạn không — dữ liệu vị trí thật từ Labs, thay heuristic so nội dung của tab Content Gap.">
					<FontAwesomeIcon icon="fa-light fa-circle-question" style={{color: '#9ca3af'}} />
				</Tooltip>
			</h3>
			{rivals.length === 0 ? (
				<Empty description="Thêm đối thủ ở tab Đối thủ (hoặc dùng Gợi ý đối thủ bên dưới) rồi quay lại so gap." />
			) : (
				<div>
					<div style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap'}}>
						<Select style={{width: 260}} placeholder="Chọn đối thủ để so" value={gapCompetitorId}
							onChange={setGapCompetitorId}
							options={rivals.map((r) => ({value: r.competitor_id, label: r.domain}))} />
						<Button primary small loading={gapLoading} disabled={!gapCompetitorId} onClick={doGap}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-arrows-left-right" />}>Tìm keyword gap</Button>
					</div>
					{gapResult && (
						<div>
							<div style={{fontSize: 13, color: '#6b7280', marginBottom: 8}}>
								{nf(gapResult.gap?.total || gapResult.gap?.rows?.length || 0)} từ khoá <strong>{gapResult.rival}</strong> có hạng mà <strong>{gapResult.own}</strong> không (hiển thị top {gapResult.gap?.rows?.length || 0} theo volume).
							</div>
							<Table rowKey="keyword" size="small" columns={gapCols} dataSource={gapResult.gap?.rows || []}
								scroll={{x: 'max-content'}} pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}} />
						</div>
					)}
				</div>
			)}

			{/* 3. Gợi ý đối thủ */}
			<h3 style={{fontSize: 15, fontWeight: 600, margin: '24px 0 10px'}}>Gợi ý đối thủ organic</h3>
			<div style={{marginBottom: 10}}>
				<Button primary small loading={suggestLoading} onClick={doSuggest}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />}>
					Tìm đối thủ từ dữ liệu Google
				</Button>
			</div>
			{suggestions && (
				<Table rowKey="domain" size="small" columns={suggestCols} dataSource={suggestions.suggestions || []}
					scroll={{x: 'max-content'}} pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}} />
			)}

			{/* Modal top từ khoá organic */}
			<Modal open={!!keywordsOf} onCancel={() => setKeywordsOf(null)} footer={null} width={820}
				title={keywordsOf ? `Top từ khoá organic: ${keywordsOf.domain}` : ''}>
				{keywordsOf && <LabsKeywordsBody projectId={projectId} row={keywordsOf} />}
			</Modal>
		</div>
	);
}

/** Nội dung modal top từ khoá organic (tách để chỉ fetch khi mở — đọc cache, 0đ). */
function LabsKeywordsBody({projectId, row}) {
	const {data, isFetching} = useGetCompetitorLabsKeywordsQuery({projectId, competitorId: row.competitor_id});

	if (isFetching) return <Loading />;
	if (!data?.has_data) return <Empty description="Chưa có dữ liệu — bấm Phân tích trước." />;

	const cols = [
		{title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword', ellipsis: true},
		{title: 'Volume', dataIndex: 'volume', key: 'volume', width: 100, align: 'center',
			sorter: (a, b) => (a.volume ?? -1) - (b.volume ?? -1), defaultSortOrder: 'descend',
			render: (v) => (v === null ? dash : volText(v))},
		{title: 'Vị trí', dataIndex: 'position', key: 'position', width: 90, align: 'center',
			sorter: (a, b) => (a.position ?? 999) - (b.position ?? 999),
			render: (v) => (v === null ? dash : <Tag color={v <= 3 ? 'green' : v <= 10 ? 'gold' : 'default'}>#{v}</Tag>)},
		{title: 'Traffic ước tính', dataIndex: 'etv', key: 'etv', width: 130, align: 'center', responsive: ['md'],
			render: (v) => (v === null ? dash : volText(Math.round(v)))},
		{title: 'Trang', dataIndex: 'url', key: 'url', ellipsis: true, responsive: ['lg'],
			render: (v) => (v ? <a href={v} target="_blank" rel="noreferrer" style={{fontSize: 12}}>{v}</a> : dash)},
	];

	return (
		<div>
			<div style={{fontSize: 13, color: '#6b7280', marginBottom: 8}}>
				Tổng {nf(data.keywords?.total || 0)} từ khoá organic · hiển thị top {data.keywords?.rows?.length || 0} theo volume
				{data.fetched_at ? ` · cập nhật ${data.fetched_at}` : ''}
			</div>
			<Table rowKey="keyword" size="small" columns={cols} dataSource={data.keywords?.rows || []}
				scroll={{x: 'max-content', y: 420}} pagination={false} />
		</div>
	);
}

export default CompetitorLabs;
